import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

const LAST_UPDATED = '2026-07-04'

const Terms = () => {
  // The footer's "Risk disclosure" link lands on #disclaimers.
  const { hash } = useLocation()
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView?.()
  }, [hash])

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8 space-y-6">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </Link>

        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md p-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Not legal advice.</strong> These Terms reflect our current policies and have been
            updated to include creator regulatory compliance obligations (Section 11). Consult a
            qualified securities attorney regarding your specific obligations before publishing
            financial signals.
          </p>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">Terms of Service</h1>
          <p className="text-sm text-gray-500 mt-1">Last updated: {LAST_UPDATED}</p>
        </div>

        <section className="space-y-4 text-sm text-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">1. Acceptance of Terms</h2>
            <p>
              By creating an account or using FinCreator (the "Service"), you agree to be bound by
              these Terms of Service. If you do not agree, do not use the Service.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">2. Description of Service</h2>
            <p>
              FinCreator is a platform for financial content creators to publish content, trading
              signals, and educational material, and to monetize their audience through
              subscriptions, tips, and a marketplace. Trading signals, performance data, and other
              financial content shared on the Service are for informational and educational
              purposes only and do not constitute investment advice. Past performance is not
              indicative of future results. You are solely responsible for your own investment
              decisions.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">3. User Accounts</h2>
            <p>
              You must provide accurate information when creating an account and are responsible
              for maintaining the confidentiality of your credentials. You must be at least 18
              years old to use the Service.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">4. Payments and Payouts</h2>
            <p>
              Payments, subscriptions, tips, and creator payouts are processed through Stripe and
              Stripe Connect. By using paid features, you agree to Stripe's terms of service in
              addition to these Terms. FinCreator may charge a platform fee on transactions as
              disclosed at the time of the transaction.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">5. Content and Intellectual Property</h2>
            <p>
              You retain ownership of content you create and publish on the Service. By posting
              content, you grant FinCreator a license to host, display, and distribute it as
              necessary to operate the Service. You are responsible for ensuring you have the
              rights to any content you upload.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">6. Prohibited Use</h2>
            <p>
              You may not use the Service to distribute misleading financial advice presented as
              guaranteed outcomes, violate securities laws, infringe intellectual property,
              harass other users, or attempt to circumvent platform fees or security controls.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">7. Termination</h2>
            <p>
              We may suspend or terminate your account for violation of these Terms. You may
              request deletion of your account at any time from your{' '}
              <Link to="/settings" className="text-primary-600 hover:underline">
                Settings
              </Link>{' '}
              page.
            </p>
          </div>

          <div id="disclaimers" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              8. Disclaimers and Limitation of Liability
            </h2>
            <p>
              The Service is provided "as is" without warranties of any kind. FinCreator is not a
              registered investment advisor and does not guarantee the accuracy of any signal,
              forecast, or performance metric shared by creators. To the maximum extent permitted
              by law, FinCreator's liability for any claim related to the Service is limited.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">9. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which FinCreator is
              incorporated, without regard to conflict-of-law principles.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">10. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Continued use of the Service after
              changes take effect constitutes acceptance of the revised Terms.
            </p>
          </div>

          <div className="border border-gray-200 rounded-md p-5 bg-gray-50 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">
              11. Creator Regulatory Compliance Responsibility
            </h2>
            <p>
              <strong>11.1 Platform role.</strong> FinCreator is a technology platform that provides
              tools for content creation, signal generation, scheduling, and distribution.
              FinCreator is <em>not</em> a registered investment adviser, broker-dealer, commodity
              trading adviser, or financial planner, and does not provide personalised investment
              advice. Nothing on the platform constitutes a recommendation to buy or sell any
              security, commodity, cryptocurrency, or other financial instrument.
            </p>
            <p>
              <strong>11.2 Creator responsibility.</strong> If you publish trading signals,
              trade ideas, portfolio performance, or other financial content to paying or free
              subscribers ("Creator Content"), you — not FinCreator — are solely responsible for:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2 text-sm">
              <li>
                Determining whether your activities require registration as an investment adviser
                under the Investment Advisers Act of 1940, applicable state Blue Sky laws, or
                equivalent regulations in your jurisdiction;
              </li>
              <li>
                Obtaining and maintaining any required licences, registrations, or exemptions before
                distributing Creator Content;
              </li>
              <li>
                Ensuring Creator Content complies with all applicable securities, commodities, and
                financial-promotion laws, including rules against market manipulation, front-running,
                and fraudulent or misleading statements;
              </li>
              <li>
                Displaying clear, prominent disclaimers to your subscribers stating that Creator
                Content is for informational and educational purposes only, does not constitute
                personalised investment advice, and that past performance is not indicative of future
                results.
              </li>
            </ul>
            <p>
              <strong>11.3 No personalised advice.</strong> You agree not to use FinCreator to
              deliver investment recommendations tailored to the individual financial situation,
              risk tolerance, or investment objectives of specific subscribers without first
              obtaining all required regulatory authorisations.
            </p>
            <p>
              <strong>11.4 Indemnification.</strong> You agree to defend, indemnify, and hold
              harmless FinCreator, its officers, directors, employees, and agents from and against
              any claims, liabilities, damages, fines, or expenses (including reasonable legal
              fees) arising out of or related to: (a) your failure to comply with applicable
              regulatory requirements; (b) any Creator Content you publish; or (c) any claim by a
              subscriber, regulator, or third party arising from your use of the Service to
              distribute financial content.
            </p>
            <p>
              <strong>11.5 Suspension and termination.</strong> FinCreator reserves the right to
              suspend or permanently terminate your account and remove Creator Content without
              prior notice if we reasonably believe you are operating in violation of applicable
              securities laws or the obligations in this Section. We may also cooperate fully with
              any regulatory investigation.
            </p>
            <p>
              <strong>11.6 No liability for Creator Content.</strong> FinCreator assumes no
              responsibility for the accuracy, completeness, or legality of Creator Content and
              shall not be liable to any subscriber or third party for losses arising from reliance
              on Creator Content.
            </p>
          </div>
        </section>

        <p className="text-xs text-gray-400 pt-4 border-t">
          Questions about these Terms? See our{' '}
          <Link to="/faq" className="text-primary-600 hover:underline">
            FAQ
          </Link>{' '}
          or contact support.
        </p>
      </div>
    </div>
  )
}

export default Terms
